import type { CompaniesControllerCreateCompanyMutationRequest } from '@proposify/document-service-api-client/dist/types/CompaniesControllerCreateCompany';
import type { CompanyResponse } from '@proposify/document-service-api-client/dist/types/CompanyResponse';
import type { ContactResponse } from '@proposify/document-service-api-client/dist/types/ContactResponse';
import type { ContactsControllerCreateContactMutationRequest } from '@proposify/document-service-api-client/dist/types/ContactsControllerCreateContact';
import { variablesBffClient } from 'services/api';
import { Endpoints } from 'utils/endpoints';
import type { CompanyAndContactsRepository } from './CompanyAndContactsRepository';
import {
  mapCompanyDtoToCompanyResponse,
  mapContactDtoToContactResponse,
  mapCreateCompanyRequestToDto,
  mapCreateContactRequestToDto,
  type CompanyDto,
  type CompositeEntityId,
  type ContactDto,
  type CrmCompany,
  type CrmContact,
} from './mappers';

export class ApiCompanyAndContactsRepositoryClass implements CompanyAndContactsRepository {
  public async createContact(payload: ContactsControllerCreateContactMutationRequest): Promise<ContactResponse> {
    const dto = await variablesBffClient.post<ContactDto>(Endpoints.createContact, mapCreateContactRequestToDto(payload));
    return mapContactDtoToContactResponse(dto);
  }

  public async createCompany(payload: CompaniesControllerCreateCompanyMutationRequest): Promise<CompanyResponse> {
    const dto = await variablesBffClient.post<CompanyDto>(Endpoints.createCompany, mapCreateCompanyRequestToDto(payload));
    return mapCompanyDtoToCompanyResponse(dto);
  }
  public async getCompanies(params?: { contactId?: string }): Promise<CompanyResponse[]> {
    const dto = await variablesBffClient.get<CompanyDto[]>(Endpoints.getCompanies, {
      params: params?.contactId ? { contact_id: params.contactId } : undefined,
    });
    return dto.map(mapCompanyDtoToCompanyResponse);
  }

  public async getContacts(params?: { companyId?: string }): Promise<ContactResponse[]> {
    const dto = await variablesBffClient.get<ContactDto[]>(Endpoints.getContacts, {
      params: params?.companyId ? { company_id: params.companyId } : undefined,
    });
    return dto.map(mapContactDtoToContactResponse);
  }

  public async searchCrmCompanies(params?: { searchTerm?: string }): Promise<CrmCompany[]> {
    return await variablesBffClient.get<CrmCompany[]>(Endpoints.searchCrmCompanies, {
      params: { q: params?.searchTerm },
    });
  }

  public async getCrmCompanyById(id: CompositeEntityId): Promise<CrmCompany> {
    return await variablesBffClient.get<CrmCompany>(Endpoints.getCrmCompanyById(id));
  }

  public async getCrmCompaniesByContactId(contactId: CompositeEntityId): Promise<CrmCompany[]> {
    return await variablesBffClient.get<CrmCompany[]>(Endpoints.getCrmCompaniesByContactId(contactId));
  }

  public async searchCrmContacts(params?: { searchTerm?: string }): Promise<CrmContact[]> {
    return await variablesBffClient.get<CrmContact[]>(Endpoints.searchCrmContacts, {
      params: { q: params?.searchTerm },
    });
  }

  public async getCrmContactById(id: CompositeEntityId): Promise<CrmContact> {
    return await variablesBffClient.get<CrmContact>(Endpoints.getCrmContactById(id));
  }

  public async getCrmContactsByCompanyId(companyId: CompositeEntityId): Promise<CrmContact[]> {
    return await variablesBffClient.get<CrmContact[]>(Endpoints.getCrmContactsByCompanyId(companyId));
  }
}

export const ApiCompanyAndContactsRepository = new ApiCompanyAndContactsRepositoryClass();
